import {SortOrder} from 'antd/lib/table';
import {ItemType} from "../../item-details/itemType";
import {Role} from "../../account/AccountProvider";
import {DateRange} from "../../common/dtos";
import {InvoiceEntry} from "./summaries";
import {InvoiceSource, InvoiceState, InvoiceType} from "../../invoicing/invoice";

export interface ErrorResponse {
  message: string;
  status?: number;
  details?: {
    message: string;
    path: string;
  };
}

export interface LoginResponse {
  userName: string;
  token: string;
  role: Role;
  defaultCompanyId: number;
}

export interface Company {
  id: number;
  name: string;
  namingPrefix?: string;
}

export interface GridRequestFilters {
  [key: string]: string[];
}

export interface FilterRequest {
  filters?: GridRequestFilters;
}

export interface GridRequest {
  page?: number;
  pageSize?: number;
  sortOrder?: SortOrder;
  sortColumnKey?: string;
  filters?: GridRequestFilters;
  deleted?: boolean;
}

export interface PageResponseEnvelope<T> {
  results: PageResponse<T>;
}

export interface PageResponse<T> {
  currentPage: number;
  content: T[];
  total: number;
}

export interface GetCategoriesResponse {
  categories: string[];
}

export interface MaintenanceTypesResponse {
  types: string[];
}

export interface SaveItemAlertRequest {
  itemId: number;
  name: string;
  alertDate: string;
  handledDate: string | null;
  operationHours: number | null;
}

export interface ItemAlert {
  id: number;
  item: Item;
  name: string;
  alertDate: string;
  handledDate: string | null;
  operationHours: number | null;
  state: string;
}

export interface FinalizeItemAlertsRequest {
  handledDate: string;
  alertItemIds: number[];
}

export interface Item {
  id?: number;
  itemType: ItemType;
  inventoryNumber?: string;
  name?: string;
  category?: string;
  resource?: string;
  details?: string;
  comment?: string;
  invoicingSuffix?: string;
  serial?: string;
  value?: number;
  acquisitionValue?: number;
  acquisitionCurrency?: string;
  acquisitionExchangeRate?: number;
  state?: string;
  priceDaily?: number;
  priceHourly?: number;
  priceMonthly?: number;
  priceMm?: number;
  pricePcs?: number;
  deleted?: boolean;
  infiniteStock?: boolean;
  inMaintenance?: boolean;
  checklistRequired?: boolean;
  external: boolean;
  externalOwner?: string;
}

export interface SaveInMaintenanceRequest {
  inMaintenance: boolean;
}

export interface MaintenanceTodos {
  entries: MaintenanceTodoEntry[];
}

export interface MaintenanceTodoEntry {
  checked: boolean;
  comment: string | null;
}

export interface MaintenanceSheet {
  entries: MaintenanceEntry[];
}

export interface MaintenanceEntry {
  key: string;
  value: string | null;
}

export interface MaintenanceHistoryResponse {
  pagedResponse: PageResponseEnvelope<MaintenanceHistory>;
  stats: {
    count: number;
    total: number;
  };
}

export interface MaintenanceHistory {
  id: number | null;
  date: string;
  name: string;
  operationHours: number | null;
  totalCost: number;
  costBearer: string | null;
  workerNames: string | null;
  errorDescription: string | null;
  workDescription: string | null;
  comment: string | null;
  travelDistance: number | null;
  travelCost: number | null;
  repairTime: number | null;
  repairCost: number | null;
  contactName: string | null;
  contactPhone: string | null;
  entries: MaintenanceHistoryItemEntry[];
  handled: boolean;
  finalized: boolean;
  attachmentCount: number;
  item?: Item;
  client?: ClientSummary;
  site?: Site;
  invoice?: InvoiceLink;
}

export const COST_BEARER_OWN = "OWN";
export const COST_BEARER_RENTER = "RENTER";

export interface MaintenanceHistoryItemEntry {
  id?: number;
  infiniteInventoryStockId?: number;
  inventoryStockItem?: InventoryStockItem;
  groupedItems?: GroupedInventoryStockItemForCostInfo[];
  inventoryNumber: string | null;
  inventoryStockId?: number;
  name: string;
  amount: number;
  cost: number;
  originalCost: number;
  discount?: number;
}

export interface GroupedInventoryStockItemForCostInfo {
  amount: number;
  initialCost: number;
  profitMargin: number;
}

export interface CreateMaintenanceHistoryItemEntry {
  inventoryStockItemId?: number,
  infiniteInventoryStockId?: number,
  infiniteInventoryStockAmount: number
}

export interface SaveMaintenanceHistoryCost {
  cost?: number;
  discount?: number;
}

export interface ClientSummary {
  id: number | null;
  name: string;
  zip: string;
  city: string;
  address: string;
  email: string | null;
  taxNumber: string | null;
  comment: string | null;
  responsibleUser: string | null;
  warning: boolean;
}

export interface Client {
  id: number | null;
  name: string;
  zip: string;
  city: string;
  address: string;
  comment: string | null;
  // discountPercent: number | null;
  email: string | null;
  tel: string | null;
  bankAccount: string | null;
  taxNumber: string | null;
  regNumber: string | null;
  idNumber: string | null;
  mothersName: string | null;
  responsibleUser: string | null;
  warning: boolean;
  deleted?: boolean | null;
}

export interface ClientWarningRequest {
  warning: boolean;
}

export interface SaveClientResponse {
  clientId?: number;
}

export interface Contact {
  id: number | null;
  name: string;
  title: string | null;
  tel: string | null;
  email: string | null;
  idNumber: string | null;
  mothersName: string | null;
  deleted: boolean;
}

export interface Site {
  id: number | null;
  address: string;
  name: string | null;
}

export interface Rental {
  id?: number;
  idString?: string;
  created?: string;
  transportCost?: number;
  client?: ClientSummary;
  representative?: Contact;
  responsible?: Contact;
  site?: Site;
  paymentType?: string;
  rentalItems?: RentalItem[];
  comment?: string;
  caution?: number;
  deleted?: boolean;
  createdBy?: string;
  lastChangedBy?: string;
  lastChangedAt?: string;
}

export interface SaveRentalResponse {
  rentalId?: number;
}

export interface RentalItem {
  id: number | null;
  item: Item;
  price: number;
  caution?: number;
  amountType: string;
  rentalAbrasionMm: number | null;
  rentalOperationHours: number | null;
  returnalAbrasionMm: number | null;
  returnalOperationHours: number | null;
  state?: string;
  rentalSite: Site | null;
  comment: string | null;
  rentalChecklistId: number | null;
  returnalChecklistId: number | null;
}

export const enum ChecklistState {
  NOT_REQUIRED = 'NOT_REQUIRED',
  MISSING = 'MISSING',
  MISSING_ATTACHMENTS = 'MISSING_ATTACHMENTS',
  READY = 'READY',
  SENT_OUT = 'SENT_OUT',
  ACCEPTED = 'ACCEPTED',
  IGNORED = 'IGNORED',
}


export interface ChecklistSelection {
  state: ChecklistState;
  id: number | null;
  createdAt: string | null;
  sendoutAt: string | null;
  acceptedAt: string | null;
}

export interface RentalGridItem {
  id: number;
  hasRentalTransportLetter: boolean;
  hasReturnalTransportLetter: boolean;
  client: {
    id: number;
    name: string;
  };
  item: {
    id: number;
    name: string;
    inventoryNumber: number;
    inMaintenance: boolean;
  };
  rental: IdsAndDate;
  returnal: null | IdsAndDate;
  rentalChecklist: ChecklistSelection;
  returnalChecklist: ChecklistSelection;
  state: string;
  caution: number;
}

export interface IdsAndDate {
  id: number;
  idString: string;
  date: string;
}

export interface Returnal {
  id?: number;
  idString?: string;
  created?: string;
  client?: ClientSummary;
  responsible?: Contact;
  transportCost?: number;
  rentalItems?: RentalItem[];
  comment?: string;
  caution?: number;
  createdBy?: string;
  lastChangedBy?: string;
  lastChangedAt?: string;
}

export interface SaveReturnalResponse {
  returnalId: number;
}

export interface GetElapsedAmountRequest {
  amountType: string;
}

export interface GetElapsedAmountResponse {
  amount?: number;
}

export interface ClientRevenueSummaryGridRequest {
  clientId: number;
  year: number;
  month: number;
}

export interface ClientRevenueEntry {
  id: number;
  entityId: number;
  name: string;
  date: string;
  finalized: boolean;
  total: number;
  outstandingTotal: number;
  invoicedTotal: number;
  invoices: InvoiceLink[];
}

export interface ClientRevenueTotals {
  maintenances: number;
  sales: number;
  manualInvoices: number;
  total: number;
}

export interface ClientRevenueSummaryGridResponse {
  client: ClientSummary;
  sales: ClientRevenueEntry[];
  maintenances: ClientRevenueEntry[];
  manualInvoices: ClientRevenueEntry[];
  total: ClientRevenueTotals;
  invoiced: ClientRevenueTotals;
  finalized: ClientRevenueTotals;
  outstanding: ClientRevenueTotals;
}

export interface RevenueSummaryRequest {
  grid: GridRequest;
  year: number;
  month: number;
}

export interface RevenueSummary {
  client: ClientSummary;
  /** Number of revenue entries in the selected month. */
  total: number;
  /** Number of revenue entries with at least one sent invoice. */
  invoicedTotal: number;
  /** Number of revenue entries without a valid sent invoice. */
  uninvoicedTotal: number;
}

export interface RevenueSummaryResponse extends PageResponseEnvelope<RevenueSummary> {}

export interface RevenueTotalRequest {
  year: number;
  month: number;
}

export interface RevenueTotal {
  total: number;
}

export interface RentalSummaryRequest {
  grid: GridRequest;
  year: number;
  month: number;
}

export interface RentalSummary {
  client?: ClientSummary;
  items?: number;
  nonFinalized?: number;
}

export interface RentalSummaryResponse extends PageResponseEnvelope<RentalSummary> {
  sum: number;
}

export interface SiteSummary {
  site: Site | null;
  count: number;
  total: number;
}

export interface TodoEntry {
  checked: boolean;
  text: string;
}

export interface Todo {
  entries: TodoEntry[];
}

export interface GetAmountAndTotalForOverrideRequest {
  rentalItemId: number;
  year: number;
  month: number;
  amountTypeOverride: string;
  priceOverride: number;
}

export interface AmountAndTotal {
  amount: number | null;
  total: number | null;
}

export interface GetTotalForOverrideRequest extends GetAmountAndTotalForOverrideRequest {
  amountOverride: number | null;
}

export interface Total {
  total: number | null;
}

export interface TransportDto {
  id: string | null;
  company: string | null;
  driver: string | null;
  mothersName: string | null;
  idNumber: string | null;
  start: string | null;
  end: string | null;
  licensePlate: string | null;
}

export interface InventoryTransportLetter {
  id: number;
  transportLetterNumber: string;
  date: string;
  incomingInvoice?: IncomingInvoice;
  responsibleUser: string | null;
  attachmentCount?: number;
  partner: Partner;
}

export interface SaveInventoryTransportLetterRequest {
  transportLetterNumber: string;
  date: string;
  incomingInvoiceId?: number;
  partnerName: string;
  responsibleUser?: string;
}

export interface SaveInventoryTransportLetterResponse {
  updatedStockItemInvoiceCount?: number;
}

export interface SaveTodoRequest {
  rentalItemId: number;
  year: number;
  month: number;
  todo: Todo;
}

export interface FinalizeSummariesRequest {
  sourceIds: number[];
  clientId: number;
  year: number;
  month: number;
}

export interface RevertFinalizedSummaryRequest {
  sourceId: number;
  clientId: number;
  year: number;
  month: number;
}

export interface YearMonthFilter {
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
}

export interface GetInvoicesRequest extends YearMonthFilter {
  grid: GridRequest;
  deleted: boolean;
}

export interface InvoicesSummary {
  client: ClientSummary;
  totalInvoiceCount: number;
  createdInvoiceCount: number;
}

export interface ClientInvoicesRequest extends YearMonthFilter {
  grid: GridRequest;
  clientId: number;
  deleted: boolean;
}

export interface Invoice {
  id: number;
  customerUrl?: string;
  externalNumber?: string;
  internalNumber?: string;
  createdAt: string;
  lastChangedAt?: string;
  lastChangedBy?: string;
  paymentType: string;
  deadline: string;
  fulfillment: string;
  continuousFulfillment: boolean;
  comment: string;
  state: InvoiceState;
  source: InvoiceSource;
  type: InvoiceType;
  sourceLink?: InvoiceSourceLink;
  historyInvoices: Invoice[];
  entries?: InvoiceEntry[];
  total: number;
  client: ClientSummary;
}

export interface InvoiceLink {
  id: number;
  displayNumber: string;
  source: InvoiceSource;
  type: InvoiceType;
  state: InvoiceState;
  customerUrl?: string;
}

export interface InvoiceSourceLink {
  id?: number;
  displayNumber?: string;
  year?: number;
  month?: number;
}

export interface SaveInvoiceRequest {
  entries: InvoiceEntry[];
  paymentType: string;
  fulfillment: string;
  deadline: string;
  comment: string;
  continuousFulfillment: boolean;
}

export interface CreateInvoiceRequest {
  source: InvoiceSource;
  sourceId?: number;
  clientId: number;
  entries: InvoiceEntry[];
  year?: number;
  month?: number;
  paymentType: string;
  created: string;
  fulfillment: string;
  deadline: string;
  comment: string;
  continuousFulfillment: boolean;
}

export interface IncomingInvoice {
  id?: number;
  name: string;
  type: string;
  companyName: string;
  bankName?: string;
  bankAccountNumber: string;
  iban?: string;
  invoiceNumber: string;
  responsibleUser: string | null;
  paymentType: string;
  issueDate: string;
  completionDate?: string;
  paymentDeadline: string;
  paidDate?: string;
  sum: number;
  currency: string;
  hufSum: number;
  exchangeRate?: number;
  comment?: string;
  state?: string;
  attachments?: Attachment[];
}

export interface Partner {
  id: number;
  name: string;
  bankAccountNumber?: string;
  bankName?: string;
  iban?: string;
}

export interface Attachment {
  id: number;
  name: string;
  url: string;
}

export interface IncomingInvoiceTypes {
  types: string[];
}

export interface CurrencyExchangeRateDto {
  date?: string;
  rate?: number;
}

export interface SetToBeTransferredRequest {
  incomingInvoiceIds: number[];
}

export interface SetTransferredRequest {
  paidDate: string;
}

export interface IncomingInvoiceTotal {
  totals: Total[]
}

export interface Total {
  state: string,
  count: number,
  sum: number
}

export interface IncomingInvoiceItem extends IncomingInvoice {
  handled: boolean;
}

export const enum EventType {
  PERSONAL_MEETING = 'PERSONAL_MEETING',
  ONLINE_MEETING = 'ONLINE_MEETING',
  TODO = 'TODO',
}

export const enum EventAlertState {
  NONE = 'NONE',
  NEW = 'NEW',
  TRIGGERED = 'TRIGGERED',
  HANDLED = "HANDLED"
}

export interface Event {
  id: number | null;
  userName: string;
  client?: ClientSummary;
  contact?: Contact;
  site?: Site;
  type: EventType;
  subject: string;
  start: string;
  end: string;
  comment: string | null;
  alertDate: string | null;
  alertHandled?: boolean;
  alertState?: EventAlertState;
  completed?: boolean;
  hasAttachments?: boolean;
}

export interface GetEventsRequest {
  dateRange: DateRange;
  userName: string;
}

export interface User {
  userName?: string;
  role: Role,
  enabled: boolean,
}

export interface Warehouse {
  id?: number;
  name: string;
  createdAt?: string;
  isDefault?: boolean;
}

export interface SaveWarehouseRequest {
  id?: number;
  name: string;
  isDefault: boolean;
}

export interface InventoryStock {
  id?: number;
  name: string;
  brand: string;
  createdAt?: string;
  type: string;
  inventoryNumber: string;
  infiniteStock: boolean;
  infiniteStockCost?: number;
  infiniteStockProfitMargin?: number;
  availableCount?: number;
}

export interface SaveInventoryStockResponse {
  id?: number;
}

export interface InventoryStockItem {
  id?: number
  inventoryStock: InventoryStock;
  incomingInvoice?: IncomingInvoice;
  inventoryTransportLetter?: InventoryTransportLetter;
  state?: InventoryStockItemState;
  createdAt?: string;
  lastChangedAt?: string;
  lastChangedBy?: string;
  deletedAt?: string;
  deletedReason?: string;
  count?: number;
  cost: number;
  currency?: string;
  exchangeRate?: number;
  profitMargin: number;
  hufCost: number;
}

export interface InventoryStockItemState {
  warehouseName?: string;
  userName?: string;
  maintenanceHistoryId?: number;
  maintenanceItemName?: string;
  internallyUsed?: boolean;
  inventorySaleId?: number;
  inventorySaleNumber?: string;
  itemId?: number;
  itemName?: string;
}

export const STATE_TARGET_MAINTENANCE = 'maintenance';
export const STATE_TARGET_USER = 'user';
export const STATE_TARGET_INTERNALLY_USED = 'internallyUsed';
export const STATE_TARGET_WAREHOUSE = 'warehouse';
export const STATE_TARGET_SOLD = 'sold';

export declare type InventoryStockItemStateTargetType =
  'warehouse'
  | 'user'
  | 'internallyUsed'
  | 'maintenance'
  | 'sold';

export interface InventoryStockItemStateFilter {
  targetType?: InventoryStockItemStateTargetType;
  userId?: string;
  warehouseId?: number;
  item?: Item;
  saleId?: number
}

export interface InventoryStockItemStateFilterRequest {
  targetType?: InventoryStockItemStateTargetType;
  userId?: string;
  warehouseId?: number;
  itemId?: number;
  saleId?: number;
}

export interface InternallyUsedInventoryStock {
  inventoryStock: InventoryStock;
  total: number;
  count: number;
}

export interface InternallyUsedInventoryStockResponse {
  pagedResponse: PageResponseEnvelope<InternallyUsedInventoryStock>;
  stats: {
    count: number;
    total: number;
  };
}

export interface GetInventoryStockItemRequest extends GridRequest {
  inventoryStockId?: number;
  stateFilter?: InventoryStockItemStateFilterRequest;
}

export interface GetInventoryStocksRequest extends GridRequest {
  stateFilter?: InventoryStockItemStateFilterRequest;
}

export interface SaveInventoryStockItemRequest {
  inventoryStockId: number;
  incomingInvoiceId?: number;
  inventoryTransportLetterId?: number;
  count: number;
  cost: number;
  profitMargin: number;
  warehouseId: number;
  exchangeRate?: number;
  currency: string;
  hufCost: number;
}

export interface SaveInventoryStockItemCostRequest {
  cost: number;
  profitMargin: number;
}

export interface CreateInventoryStockItemForInternalUsageRequest {
  inventoryStockId: number;
  internallyUsedItemId?: number;
  count: number;
  cost: number;
}

export interface DeleteInventoryStockItemsRequest {
  ids: number[];
  deletedReason?: string;
}

export interface MoveInventoryStockItemsRequest {
  ids: number[];
  userId?: string;
  warehouseId?: number;
  internallyUsed?: boolean;
  internallyUsedItemId?: number;
}

export interface ChecklistProperty {
  name: string,
  value: string,
  comment?: string,
}

export interface Checklist {
  id: number | null;
  rentalItem: RentalItem;
  rental: ChecklistRelation | null;
  returnal: ChecklistRelation | null;
  properties: ChecklistProperty[];
  attachments: Attachment[];
  state: ChecklistState;
  comment: string | null;
  createdAt: string | null;
  sendoutAt: string | null;
  recipients: ChecklistRecipient[];
  acceptedAt: string | null;
  acceptedBy: EmailContact | null;
}

export interface EmailContact {
  id: number;
  name: string;
  email: string | null;
}

export interface ChecklistRecipient {
  contact: EmailContact;
  link: string;
}

export interface ChecklistRelation {
  id: number;
  idString: string;
  createdAt: string;
}

export interface ChecklistAlert {
  rentalItem: RentalItem,
  rental?: IdsAndDate,
  returnal?: IdsAndDate,
  rentalChecklist?: ChecklistSelection,
  returnalChecklist?: ChecklistSelection,
}

export interface SaveChecklist {
  id: number | null;
  rentalItemId: number;
  rentalId?: number;
  returnalId?: number;
  properties: ChecklistProperty[];
  comment: string | null;
  ignored: boolean;
}

export interface ChecklistSummaries {
  rental?: ChecklistSummary,
  returnal?: ChecklistSummary,
}

export interface ChecklistSummary {
  requiredCount: number,
  checklists: Checklist[],
}

export interface ChecklistSendRequest {
  recipientContactIds: number[];
}

export interface EmailSendResponse {
  successfulSendouts: EmailContact[];
  failedSendouts: EmailContact[];
}

export interface RentalDocumentSendRequest {
  recipientContactIds: number[];
  documentTypes: RentalDocumentType[];
}

export const enum RentalDocumentType {
  TERMS_AND_CONDITIONS = 'TERMS_AND_CONDITIONS',
  RENTAL_CONTRACT = 'RENTAL_CONTRACT',
  TRANSPORT_LETTER = 'TRANSPORT_LETTER',
  DEPOSIT_REQUEST = 'DEPOSIT_REQUEST',
  MAINTENANCE_GUIDE = 'MAINTENANCE_GUIDE',
}

export interface RentalDocumentSendout {
  sendoutAt: string | null;
  documentTypes: RentalDocumentType[];
  recipients: EmailContact[];
}

export const enum ReturnalDocumentType {
  TRANSPORT_LETTER = 'TRANSPORT_LETTER',
  RETURNAL_REPORT = 'RETURNAL_REPORT',
}


export interface ReturnalDocumentSendout {
  sendoutAt: string | null;
  documentTypes: ReturnalDocumentType[];
  recipients: EmailContact[];
}


export interface ReturnalDocumentSendRequest {
  recipientContactIds: number[];
  documentTypes: ReturnalDocumentType[];
}

export interface SaveUserRequest {
  userName: string,
  password?: string,
  role: Role,
  enabled: boolean,
}

export interface SaveInventorySaleRequest {
  clientId: number;
  createdAt: string;
  comment: string | null;
  invoiceComment: string | null;
  deliveryMethod: string | null;
  transportCost?: number;
}

export interface CreateInventorySaleEntry {
  inventoryStockItemId: number;
}

export interface InventorySale {
  id: number | null;
  createdAt: string;
  saleNumber: string;
  client?: ClientSummary;
  invoice?: InvoiceLink;
  entries: InventorySaleEntry[];
  totalCost: number;
  comment: string | null;
  invoiceComment: string | null;
  deliveryMethod: string | null;
  transportCost?: number;
  lastChangedAt?: string;
  lastChangedBy?: string;
  deletedAt?: string;
}

export interface InventorySaleNumber {
  id: number;
  saleNumber: string;
}

export interface InventorySaleEntry {
  id?: number;
  inventoryStockItem?: InventoryStockItem;
  groupedItems?: GroupedInventoryStockItemForCostInfo[];
  inventoryNumber: string | null;
  inventoryStockId?: number;
  name: string;
  amount: number;
  cost: number;
  originalCost: number;
  discount?: number;
}



// WEBPACK FOOTER //
// ./src/api/dtos/dtos.ts